import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api";
import type { Membership, User } from "../api/types";
import {
  loginWithPasskey as passkeyLogin,
  requestMagicLink,
  totpVerify,
} from "./methods";

/**
 * Session state for the logged-in-only shell. Hydrates from GET /auth/me on
 * mount; a 401 means "anonymous" (the app renders login). Every authenticated
 * route consults `status` via RouteGuard — the SPA serves no data unauthed.
 */

type Status = "loading" | "authed" | "anon";

export interface AuthState {
  status: Status;
  user: User | null;
  memberships: Membership[];
  /** Password step. Returns `{ mfaRequired }`: when true, the account has TOTP
   *  enrolled and no session is open yet — the caller collects a code and calls
   *  `completeMfa`. When false, the session is open and the shell is hydrated. */
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean }>;
  /** Second factor: verify the TOTP/recovery code against the `el_mfa` challenge
   *  cookie, open the session, and hydrate the shell. */
  completeMfa: (code: string) => Promise<void>;
  /** Discoverable passkey login (WebAuthn). Hydrates the shell on success. */
  loginWithPasskey: () => Promise<void>;
  /** Request a magic-link email (always resolves — no account enumeration). */
  requestMagic: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  /** re-hydrate after an invite-accept flow mints a session */
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

/** The raw auth context — exported ONLY so unit tests can inject a fixed session
 *  without the async `AuthProvider` hydration (which needs a live `api.me`). */
export const AuthContextForTest = Ctx;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me.user);
      setMemberships(me.memberships);
      setStatus("authed");
    } catch {
      setUser(null);
      setMemberships([]);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.login({ email, password });
      if (res.mfa_required) return { mfaRequired: true };
      await refresh();
      return { mfaRequired: false };
    },
    [refresh],
  );

  const completeMfa = useCallback(
    async (code: string) => {
      await totpVerify(code);
      await refresh();
    },
    [refresh],
  );

  const loginWithPasskey = useCallback(async () => {
    const ok = await passkeyLogin();
    if (!ok) throw new Error("passkey_cancelled");
    await refresh();
  }, [refresh]);

  const requestMagic = useCallback(async (email: string) => {
    await requestMagicLink(email);
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setMemberships([]);
    setStatus("anon");
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      memberships,
      login,
      completeMfa,
      loginWithPasskey,
      requestMagic,
      logout,
      refresh,
    }),
    [
      status,
      user,
      memberships,
      login,
      completeMfa,
      loginWithPasskey,
      requestMagic,
      logout,
      refresh,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Non-throwing session accessor: returns the auth state, or `null` when no
 *  `<AuthProvider>` is mounted. For components that merely READ the current user
 *  (e.g. the review layer's comment-visibility pill needs `is_staff`/`id` to
 *  decide `canEdit`) and must still render in provider-less contexts (isolated
 *  component tests, storybook) rather than crash. */
export function useAuthOptional(): AuthState | null {
  return useContext(Ctx);
}
